// Typed API client. One file. Mirrors the KCG pattern.

import type { FocusAreaSlug, PerformanceDomainSlug, PrincipleSlug } from "@/lib/pmbok";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// --- Auth ---------------------------------------------------------------

export type LensMode = "pmbok" | "plain";

export type UserRole =
  | "owner"
  | "admin"
  | "foreman"
  | "bookkeeper"
  | "viewer"
  | "member";

export type Capability =
  | "manage_household"
  | "manage_members"
  | "manage_projects"
  | "edit_operational"
  | "edit_financial"
  | "view_financial"
  | "view_operational";

export type Capabilities = Record<Capability, boolean>;

export interface CurrentUser {
  id: number;
  household_id: number;
  email: string;
  display_name: string;
  role: UserRole;
  lens_mode: LensMode;
  /** PMBOK lens toggle is restricted server-side to a hard-coded allowlist
   *  (just Jess for now — it's a PMP study aid, not a product feature). */
  can_set_lens_mode: boolean;
  capabilities: Capabilities;
  onboarded_at: number | null;
  household_name: string;
  household_city: string | null;
  household_region: string | null;
  household_trade: string | null;
  household_team_hint: "solo" | "small" | "crew" | null;
}

export interface SignupInput {
  email: string;
  password: string;
  display_name: string;
  household_name: string;
}

export interface UpdateMeInput {
  display_name?: string;
  lens_mode?: LensMode;
}

export interface UpdateHouseholdInput {
  name?: string;
  city?: string | null;
  region?: string | null;
}

export const auth = {
  me: () => request<{ user: CurrentUser }>("/auth/me"),
  login: (email: string, password: string) =>
    request<{ user: CurrentUser; bootstrap?: boolean }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  signup: (data: SignupInput) =>
    request<{ user: CurrentUser }>("/auth/signup", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateMe: (data: UpdateMeInput) =>
    request<{ user: CurrentUser }>("/auth/me", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
  changePassword: (current_password: string, new_password: string) =>
    request<{ ok: true }>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password, new_password }),
    }),
  forgotPassword: (email: string) =>
    request<{ ok: true }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, new_password: string) =>
    request<{ ok: true }>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, new_password }),
    }),
};

export const household = {
  update: (data: UpdateHouseholdInput) =>
    request<{ user: CurrentUser }>("/household", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
};

// --- Channels (the surfaces a workspace publishes to) -------------------

export interface Channel {
  id: number;
  slug: string;
  name: string;
  handle: string | null;
  url: string | null;
  color: string | null;
  sort_order: number;
  is_active: number;
  created_at: number;
}

export interface ChannelInput {
  slug?: string;
  name?: string;
  handle?: string | null;
  url?: string | null;
  color?: string | null;
  sort_order?: number;
  is_active?: boolean | number;
}

export interface CreateChannelInput extends ChannelInput {
  name: string;
}

export const channels = {
  list: () => request<{ channels: Channel[] }>("/channels"),
  create: (data: CreateChannelInput) =>
    request<{ id: number; slug: string; ok: true }>("/channels", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: number, data: ChannelInput) =>
    request<{ ok: true }>(`/channels/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  remove: (id: number) =>
    request<{ ok: true }>(`/channels/${id}`, { method: "DELETE" }),
};

// --- Content items (the planned / published pieces) ---------------------

export type ContentFormat =
  | "post"
  | "article"
  | "newsletter"
  | "video"
  | "short"
  | "carousel"
  | "thread"
  | "livestream";

export type ContentStatus =
  | "idea"
  | "drafting"
  | "scheduled"
  | "published"
  | "evergreen"
  | "killed";

/** pillar = PMBOK 8 Performance Domain; focus_area = Focus Area; principle = Principle. */
export type PillarSlug = PerformanceDomainSlug;
export type { FocusAreaSlug, PrincipleSlug };

export interface ContentItem {
  id: number;
  channel_id: number | null;
  title: string;
  hook: string | null;
  body: string | null;
  pillar: PillarSlug | null;
  focus_area: FocusAreaSlug | null;
  principle: PrincipleSlug | null;
  format: ContentFormat;
  status: ContentStatus;
  scheduled_for: number | null;
  published_at: number | null;
  url: string | null;
  cta: string | null;
  notes: string | null;
  sort_order: number;
  piece: string | null;
  provenance: "original" | "repost";
  created_at: number;
  updated_at: number;
  created_by: number | null;
  // Joined from channels (null when unchanneled).
  channel_slug: string | null;
  channel_name: string | null;
  channel_color: string | null;
}

export interface ContentInput {
  title?: string;
  hook?: string | null;
  body?: string | null;
  channel_id?: number | null;
  pillar?: PillarSlug | null;
  focus_area?: FocusAreaSlug | null;
  principle?: PrincipleSlug | null;
  format?: ContentFormat;
  status?: ContentStatus;
  scheduled_for?: number | null;
  published_at?: number | null;
  url?: string | null;
  cta?: string | null;
  notes?: string | null;
  sort_order?: number;
  piece?: string | null;
  provenance?: "original" | "repost";
}

export interface CreateContentInput extends ContentInput {
  title: string;
}

export const content = {
  list: () => request<{ content: ContentItem[] }>("/content"),
  create: (data: CreateContentInput) =>
    request<{ id: number; ok: true }>("/content", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: number, data: ContentInput) =>
    request<{ ok: true }>(`/content/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  remove: (id: number) =>
    request<{ ok: true }>(`/content/${id}`, { method: "DELETE" }),
  reorder: (ids: number[]) =>
    request<{ ok: true }>("/content/reorder", { method: "POST", body: JSON.stringify({ ids }) }),
};

// --- Social connections + engagement reporting --------------------------

export type SocialProvider =
  | "meta_facebook"
  | "meta_instagram"
  | "tiktok"
  | "manual_linkedin"
  | "manual_substack";
export type ConnectionStatus = "active" | "expired" | "error" | "revoked";
/** OAuth entry points (a Meta connect yields FB + IG connection rows). */
export type ConnectProvider = "meta" | "tiktok";

export interface SocialConnection {
  id: number;
  provider: SocialProvider;
  channel_id: number | null;
  external_name: string | null;
  username: string | null;
  status: ConnectionStatus;
  last_synced_at: number | null;
  last_error: string | null;
  token_expires_at: number | null;
  created_at: number;
}

export interface ConfiguredFlags {
  meta: boolean;
  tiktok: boolean;
  encryption: boolean;
}

export const connections = {
  list: () =>
    request<{ connections: SocialConnection[]; configured: ConfiguredFlags }>("/connections"),
  disconnect: (id: number) =>
    request<{ ok: true }>(`/connections/${id}`, { method: "DELETE" }),
  /** Full-page navigation target that begins the OAuth flow. */
  startUrl: (provider: ConnectProvider) => `/api/connections/${provider}/start`,
};

/** Manual performance imports (LinkedIn .xlsx / Substack .csv). The file is
 *  parsed + mapped client-side (see src/lib/imports); we POST normalized rows. */
export const imports = {
  run: (provider: "linkedin" | "substack", payload: { account: unknown[]; posts: unknown[] }) =>
    request<{ ok: true; connection_id: number; imported: { account_rows: number; post_rows: number } }>(
      `/imports/${provider}`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
};

export interface ReportChannel {
  channel_id: number | null;
  slug: string | null;
  name: string;
  color: string | null;
  followers: number | null;
  reach: number;
  impressions: number;
  engagements: number;
  posts: number;
  post_engagement: number;
  post_views: number;
}

export interface ReportTotals {
  followers: number;
  followers_change: number;
  reach: number;
  impressions: number;
  engagements: number;
  posts: number;
  post_engagement: number;
  post_views: number;
  connections: number;
}

export interface TrendPoint {
  date: string;
  reach: number;
  impressions: number;
  engagements: number;
}

export interface TopPost {
  id: number;
  channel_id: number | null;
  channel_name: string | null;
  channel_color: string | null;
  channel_slug: string | null;
  permalink: string | null;
  caption: string | null;
  media_type: string | null;
  posted_at: number | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  engagement: number | null;
  content_item_id: number | null;
}

export interface ReportsSummary {
  range: { days: number; since: string };
  channels: ReportChannel[];
  totals: ReportTotals;
  trend: TrendPoint[];
  top_posts: TopPost[];
  connections: SocialConnection[];
  configured: ConfiguredFlags;
  last_synced_at: number | null;
}

export interface SyncOutcome {
  connection_id: number;
  provider: string;
  ok: boolean;
  account_rows: number;
  post_rows: number;
  error?: string;
}

export const reports = {
  summary: (days = 30) => request<ReportsSummary>(`/reports/summary?days=${days}`),
  sync: (days = 30) =>
    request<{ ok: true; synced: number; total: number; outcomes: SyncOutcome[] }>(
      `/reports/sync?days=${days}`,
      { method: "POST" },
    ),
};
